// CustomerServiceButton.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { getIsInIframe } from '../resource/getJsEnvironment.js';
import { ButtonId } from '../resource/mixpanel.js';
import { LIVESTREAM_PATH } from '../resource/liveStreamConstants.js';
import MailTo from '../container/MailTo.js';
import defaults from '../resource/defaults.js';
import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import { push as customerServicePush } from '../resource/customerService.js';

import FadeInFromBottom from '../style/FadeInFromBottom.js';

import cnIcon from '../../img/ic-online-service-cn.svg';
import enIcon from '../../img/ic-online-service-en.svg';
import twIcon from '../../img/ic-online-service-tw.svg';
import jpIcon from '../../img/ic-online-service-jp.svg';
import koIcon from '../../img/ic-online-service-ko.svg';
import thIcon from '../../img/ic-online-service-th.svg';
import viIcon from '../../img/ic-online-service-vi.svg';
import HelpCenterIconSrc from '../../img/ic_service.svg';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import HamburgerMenuItemText from '../component/HamburgerMenuItemText.jsx';
import { hover } from '../style/media.js';

const getLngIcon = lng => {
  if (lng === 'zh-CN') {
    return cnIcon;
  } else if (lng === 'zh-TW') {
    return twIcon;
  } else if (lng === 'th') {
    return thIcon;
  } else if (lng === 'vi') {
    return viIcon;
  } else if (lng === 'ja') {
    return jpIcon;
  } else if (lng === 'ko') {
    return koIcon;
  } else {
    return enIcon;
  }
};

export const CustomerServiceButton = ({
  meId = '',
  swagSupportUrl = '',
  i18n = defaults.EMPTY_OBJECT,
  unreadCount = 0,
  sendCurrentRemoteConfig = defaults.NULL_FUNCTION,
  clearUnreadCount = defaults.NULL_FUNCTION,
  updateDataToIntercom = defaults.NULL_FUNCTION,
  isIntercomAlive = false,
  canDisplay = false,
  isUserInfoRequired = true,
  shouldOpenCustomerServiceWithChatroom = false,
  isMenuAction = false,
  t,
}) => {
  const [shouldFadeIn, setShouldFadeIn] = useState(false);
  const { pathname } = useLocation();
  const { listType } = useParams();

  useEffect(() => {
    let timeout = null;
    if (canDisplay) {
      setShouldFadeIn(true);
      timeout = setTimeout(() => setShouldFadeIn(false), 600); // TODO: remote config
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [canDisplay]);

  const unreadDisplay = 99 < unreadCount ? '99+' : `${unreadCount}`;

  if (listType && listType === LIVESTREAM_PATH) return null;

  const isUser = pathname.startsWith('/user/') || pathname.startsWith('/u/');
  if (isUser && listType && listType === 'chat') return null;

  if (getIsInIframe()) return null;

  const shouldBypassMailTo = !!(meId && swagSupportUrl);

  const { forwardedAsElement, linkTo, elementId } =
    shouldOpenCustomerServiceWithChatroom && swagSupportUrl
      ? {
          forwardedAsElement: Link,
          linkTo: swagSupportUrl,
          elementId: 'customer-service-chatroom-link',
        }
      : shouldBypassMailTo
        ? {
            forwardedAsElement: Link,
            linkTo: swagSupportUrl,
            elementId: swagSupportUrl
              ? 'swag-support-link'
              : 'intercom_launcher',
          }
        : {
            forwardedAsElement: isIntercomAlive ? 'button' : 'a',
            linkTo: undefined,
            elementId: swagSupportUrl
              ? 'swag-support-link'
              : 'intercom_launcher',
          };

  const handleClick = () => {
    sendCurrentRemoteConfig();
    if (shouldOpenCustomerServiceWithChatroom && swagSupportUrl) {
      return;
    }
    if (!swagSupportUrl) {
      updateDataToIntercom();
      clearUnreadCount();
      customerServicePush({ args: ['toggleOpen'] });
    }
  };

  const buttonContent = (
    <StyledCustomerServiceButton
      id={elementId}
      forwardedAs={forwardedAsElement}
      shouldFallbackToCustomerService={!swagSupportUrl}
      isUserInfoRequired={isUserInfoRequired}
      canDisplay={canDisplay}
      shouldFadeIn={shouldFadeIn}
      shouldBypassMailTo={shouldBypassMailTo}
      isMenuAction={isMenuAction}
      to={linkTo}
    >
      <LinkIcon
        src={isMenuAction ? HelpCenterIconSrc : getLngIcon(i18n.language)}
        alt="customer service"
        isMenuAction={isMenuAction}
        data-element_id={ButtonId.All.ButtonOnlineService}
        onClick={handleClick}
      />
      {isMenuAction && (
        <HamburgerMenuItemText>
          {t('Service_button', {
            ns: TranslationNamespace.GENERAL,
          })}
        </HamburgerMenuItemText>
      )}
      {!!unreadCount && !swagSupportUrl && !isMenuAction && (
        <UnreadBadge>{unreadDisplay}</UnreadBadge>
      )}
    </StyledCustomerServiceButton>
  );

  // When isMenuAction, wrap in clickable container to handle onClick properly
  if (isMenuAction) {
    return (
      <MenuActionWrapper
        onClick={handleClick}
        data-element_id={ButtonId.All.ButtonOnlineService}
      >
        {buttonContent}
      </MenuActionWrapper>
    );
  }

  return buttonContent;
};

CustomerServiceButton.propTypes = {
  meId: PropTypes.string,
  swagSupportUrl: PropTypes.string,
  i18n: PropTypes.object,
  unreadCount: PropTypes.number,
  sendCurrentRemoteConfig: PropTypes.func,
  clearUnreadCount: PropTypes.func,
  updateDataToIntercom: PropTypes.func,
  isIntercomAlive: PropTypes.bool,
  canDisplay: PropTypes.bool,
  isUserInfoRequired: PropTypes.bool,
  shouldOpenCustomerServiceWithChatroom: PropTypes.bool,
  isMenuAction: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

const StyledCustomerServiceButton = styled(MailTo)`
  ${({ isMenuAction }) =>
    isMenuAction
      ? `
        width: 100%;
        height: auto;
        display: flex;
        align-items: center;
        box-sizing: border-box;
        font-size: 14px;
      `
      : `
        height: 56px;
        width: 56px;
      `}

  cursor: pointer;
  position: relative;
  &:hover {
    opacity: 1;
  }

  ${({ canDisplay }) =>
    !canDisplay
      ? `
        opacity: 0;
        pointer-events: none;
      `
      : ''}
  animation: 0.6s forwards
    ${({ shouldFadeIn, isMenuAction }) =>
    shouldFadeIn && !isMenuAction ? FadeInFromBottom : ''};
`;

const MenuActionWrapper = styled.div`
  width: 100%;
  cursor: pointer;
  ${hover`
    > a {
    opacity: 1
  }
  `}
`;

const LinkIcon = styled.img`
  ${({ isMenuAction }) =>
    isMenuAction
      ? `
        width: 24px;
        height: 24px;
        margin-right: 8px;
      `
      : `
        height: 56px;
        width: 56px;
        filter: drop-shadow(0px 2px 4px black);
      `}
`;

const UnreadBadge = styled.div`
  position: absolute;
  top: -4px;
  right: 0px;
  height: 20px;
  min-width: 20px;
  border-radius: 10px;
  background-color: #cf0404;
  padding: 0px 4px;
  text-align: center;
  font-size: 12px;
  line-height: 20px;
`;

export default withTranslation()(CustomerServiceButton);
